/**
 * competitionAnalysis.js — what VirTour Media, Framed by JG and 805 Media Works
 * would charge for the order on the quote builder, line by line
 *
 * Each quote line is matched through competitorMap.js to the products a buyer
 * would tick on that competitor's form, priced at the quote's square footage
 * on their current price list. Line by line by decision (Cyprian, 2026-11-04):
 * no package deals, even where a competitor's bundle would come in cheaper.
 *
 * Order-level rules still apply where a buyer would meet them on the form:
 * - VirTour's "+" (with another service) price: a product that has one bills
 *   at it when another VirTour service is on the same comparison, as the
 *   portal's VirTour mode does, with two refinements from their form text: a
 *   "+"-only row (a website, property lines) is not a service for this, and
 *   VT_ADDON_NEEDS holds the narrower conditions their form names (the reel,
 *   the FPV tour and the agent tour need a listing video).
 * - An `includedIn` slot compares as included when its host product is on the
 *   comparison (Framed by JG's Photography brings the aerials, so our separate
 *   Aerial Photos line costs them nothing), else as not offered. An
 *   `includedWith` slot is included beside any of its hosts, else priced.
 * - A Framed by JG add-on without the service group it hangs off (photo
 *   add-ons need their Photography, video add-ons a video), or an 805 Media
 *   Works add-on with none of their main services, is still priced, but
 *   flagged: their form wouldn't show it.
 * - Quantities: a product line's qty buys that many of theirs; an add-on line
 *   multiplies only where theirs is per photo or the slot says `perLine`.
 *   VirTour's staging drops to $40 a photo from 4.
 * Every other row prices on its own, so two of our lines that map to the same
 * product of theirs (a reel and an extra reel) are two of theirs.
 */

import { COMPETITOR_MAP } from '../data/competitorMap';
import { findVtProduct, vtGetPrice, getVtAddonPrice, VIRTOUR_DB, VT_PRICE_VERSIONS } from '../data/virtourCatalog';
import { findJgProduct, jgGetPrice, jgTierIndex, JG_TIER_LABELS, JG_AS_OF } from '../data/framedByJgCatalog';
import { findMwProduct, mwGetPrice, mwRangeLabel, MW_AS_OF } from '../data/mediaworks805Catalog';
import { SVC_DB_NEW, rgFindProduct, rgGetPrice, rgAddonPrice, rgPriceContext, RG_CURRENT_VERSION } from '../data/serviceCatalog';

function round2(n) {
  return Math.round(n * 100) / 100;
}

const VT_AS_OF = VT_PRICE_VERSIONS.find((v) => v.id === 'current').asOf;

export const COMPETITORS = [
  {
    id: 'vt',
    name: 'VirTour Media',
    asOf: VT_AS_OF,
    find: (id) => findVtProduct(id)?.product ?? null,
    tierLabel: (sqft) => {
      const n = Math.floor(Number(sqft) || 0);
      return n >= 10000
        ? '10,000+ sq ft: they quote by phone, top rung shown'
        : VIRTOUR_DB.tierLabels[Math.min(Math.floor(n / 1000), 9)];
    },
  },
  {
    id: 'jg',
    name: 'Framed by JG',
    asOf: JG_AS_OF,
    find: findJgProduct,
    tierLabel: (sqft) => {
      const t = jgTierIndex(sqft);
      return t == null ? 'Over 8,000 sq ft: they inquire' : JG_TIER_LABELS[t];
    },
  },
  {
    id: 'mw',
    name: '805 Media Works',
    asOf: MW_AS_OF,
    find: findMwProduct,
    tierLabel: mwRangeLabel,
  },
];

// A hand line on an RE quote (a sourceless legacy line loads as 're' with no
// productId) has nothing to match, like a custom line.
const isCatalogLine = (line) => (line.source === 're' || line.source === 're-addon') && !!line.productId;

const VT_LISTING_VIDEOS = ['vt_highlight_video', 'vt_cinematic_video', 'vt_luxury_video', 'vt_full_production_film'];

// VirTour products whose "+" price their form ties to something narrower than
// "another service" (their form, read 2026-09-20 and 2026-11-04): the reel
// "saves $50 if we are also creating a wide listing video"; the FPV tour gets
// "Add On Pricing" "if we are already shooting a listing video"; the agent tour
// is for one "already getting a property video for the MLS".
const VT_ADDON_NEEDS = {
  vt_vertical_reel: [...VT_LISTING_VIDEOS, 'vt_agent_guided_tour'],
  vt_fpv_tour: VT_LISTING_VIDEOS,
  vt_agent_guided_tour: VT_LISTING_VIDEOS,
};

// Sold only as "+" rows on their form, so none of these is the other service a
// "+" price needs: a website doesn't make twilight an add-on.
const VT_PLUS_ONLY = new Set([
  'vt_property_lines', 'vt_location_markers', 'vt_virtual_twilight', 'vt_website_custom_domain',
  'vt_website_vtm_domain', 'vt_website_standard_domain', 'vt_agent_narration', 'vt_optimized_vertical_reel',
  'vt_fpv_clips', 'vt_twilight_video', 'vt_branded_version', 'vt_inspire3_upgrade',
  'vt_add_2d_floor_plan', 'vt_add_3d_floor_plan', 'vt_adu_floor_plan', 'vt_adu_2d_floor_plan',
]);

// Per-photo volume breaks: "$50/photo, or $40/photo when you order 4 or more".
const VT_VOLUME = {
  vt_virtually_staged_image: { from: 4, unit: 40 },
};

const JG_ADDON_WARNING = {
  photo: 'They only sell it beside their Photography.',
  video: 'They only sell it beside one of their videos.',
  any: 'They only sell it beside one of their services.',
};

const itemSpec = (item) => (typeof item === 'string' ? { id: item } : item);

/**
 * @param {object} args
 * @param {Array} args.lines   the quote's active lines (computed.activeLines)
 * @param {number|string} args.sqft
 * @returns {{ rows, totals, competitors, sqft, sqftMissing }}
 */
export function buildCompetitionAnalysis({ lines = [], sqft = 0 }) {
  const sqftNum = Math.floor(Number(sqft) || 0);
  const entryOf = (line) => (isCatalogLine(line) ? COMPETITOR_MAP[line.productId] : null);

  // Everything each competitor's form would hold for this order, for the two
  // order-level rules (VirTour's "+" price, Framed by JG's included aerials).
  const onOrder = { vt: new Map(), jg: new Map(), mw: new Map() };
  for (const line of lines) {
    const entry = entryOf(line);
    for (const c of COMPETITORS) {
      for (const item of entry?.[c.id]?.items || []) {
        const { id } = itemSpec(item);
        const product = c.find(id);
        if (product) onOrder[c.id].set(id, product);
      }
    }
  }
  const vtNeedsMet = (id) => VT_ADDON_NEEDS[id].some((other) => onOrder.vt.has(other));
  // Bills as a service of its own: no "+" price and not a "+"-only row, or a
  // "+" product whose own condition is unmet, so it bills standalone.
  const vtIsService = (id, product) => {
    if (VT_PLUS_ONLY.has(id)) return false;
    if (product.addonPrice === undefined) return true;
    return !!VT_ADDON_NEEDS[id] && !vtNeedsMet(id);
  };
  const vtRidesAlong = (id) => {
    if (VT_ADDON_NEEDS[id]) return vtNeedsMet(id);
    for (const [otherId, product] of onOrder.vt) {
      if (otherId !== id && vtIsService(otherId, product)) return true;
    }
    return false;
  };
  const unitPrice = (cid, product) => {
    if (cid === 'jg') return jgGetPrice(product, sqftNum);
    if (cid === 'mw') return mwGetPrice(product, sqftNum);
    return product.addonPrice !== undefined && vtRidesAlong(product.id)
      ? getVtAddonPrice(product, sqftNum)
      : vtGetPrice(product, sqftNum);
  };

  // Framed by JG shows photo add-ons only beside Photography, video add-ons
  // only beside one of their videos, and extras beside any service.
  const jgKinds = new Set([...onOrder.jg.values()].filter((p) => !p.addon).map((p) => p.kind));
  const jgUnsellable = (p) => !!p.addon && (p.addon === 'any' ? jgKinds.size === 0 : !jgKinds.has(p.addon));
  // 805 Media Works sells add-ons only beside one of their main services.
  const mwHasMain = [...onOrder.mw.values()].some((p) => p.main);

  const rows = lines.map((line) => {
    const qty = Number(line.qty) || 0;
    const ours = round2(qty * (Number(line.rate) || 0));
    const entry = entryOf(line);

    let flash = null;
    if (entry?.flashRef) {
      const ref = rgFindProduct(entry.flashRef, RG_CURRENT_VERSION)?.product;
      const price = ref ? rgGetPrice(ref, sqftNum, RG_CURRENT_VERSION).price : null;
      // The Old list sells no Flash Photos, so the reference says where it's from.
      const name = line.priceVersion === RG_CURRENT_VERSION ? ref?.name : `${ref?.name} (New list)`;
      if (price != null) flash = { name, price: round2(price * qty) };
    }

    const cells = {};
    for (const c of COMPETITORS) {
      const slot = entry?.[c.id];
      if (!entry) {
        cells[c.id] = { status: 'unmapped', note: isCatalogLine(line) ? 'Not mapped yet.' : 'Custom line.' };
        continue;
      }
      if (!slot) {
        cells[c.id] = { status: 'none', note: entry.none?.[c.id] || '' };
        continue;
      }
      if (slot.includedIn) {
        const host = c.find(slot.includedIn);
        if (!onOrder[c.id].has(slot.includedIn)) {
          cells[c.id] = { status: 'none', note: slot.note || '' };
        } else if (host && unitPrice(c.id, host) == null) {
          // The host is quoted at this size, so what it brings is too.
          cells[c.id] = { status: 'inquire', items: [], note: `Comes with their ${host.name}, which they quote at this size.` };
        } else {
          cells[c.id] = { status: 'included', note: slot.note || `In their ${host?.name ?? slot.includedIn}.` };
        }
        continue;
      }
      const withHost = slot.includedWith?.find((id) => onOrder[c.id].has(id));
      if (withHost) {
        cells[c.id] = { status: 'included', note: `Included in their ${c.find(withHost)?.name ?? withHost}.` };
        continue;
      }
      if (!slot.items.length) {
        // Only ever comes inside something of theirs, which isn't here.
        cells[c.id] = { status: 'none', note: slot.note || '' };
        continue;
      }

      const items = [];
      let price = 0;
      let inquire = false;
      let warning = '';
      for (const raw of slot.items) {
        const { id, perLine } = itemSpec(raw);
        const product = c.find(id);
        if (!product) continue;
        // A product line ships `qty` copies (three Dynamic Reels), so theirs
        // does too; an add-on line counts only where it is per photo or per cut.
        const n = product.perImage || perLine || line.source === 're' ? Math.max(qty, 1) : 1;
        const volume = c.id === 'vt' ? VT_VOLUME[id] : null;
        const unit = volume && n >= volume.from ? volume.unit : unitPrice(c.id, product);
        const asAddon = c.id === 'vt' && product.addonPrice !== undefined && vtRidesAlong(id);
        if (unit == null) inquire = true;
        else price += unit * n;
        if (c.id === 'jg' && jgUnsellable(product)) warning = JG_ADDON_WARNING[product.addon];
        if (c.id === 'mw' && !product.main && !mwHasMain) warning = 'They only sell it beside one of their main services.';
        items.push({ name: product.name, qty: n, unit, asAddon });
      }

      const note = [slot.note, warning].filter(Boolean).join(' ');
      cells[c.id] = inquire
        ? { status: 'inquire', items, note }
        : { status: 'priced', items, price: round2(price), note };
    }

    return {
      key: line.id ?? line.sid ?? line.desc,
      desc: line.desc,
      isAddon: line.source === 're-addon',
      qty,
      ours,
      flash,
      cells,
    };
  });

  const ours = round2(rows.reduce((s, r) => s + r.ours, 0));
  const totals = { ours };
  for (const c of COMPETITORS) {
    let theirs = 0;
    let oursMatched = 0;
    let unmatched = 0;
    let inquire = 0;
    for (const r of rows) {
      const cell = r.cells[c.id];
      if (cell.status === 'priced') {
        theirs += cell.price;
        oursMatched += r.ours;
      } else if (cell.status === 'included') {
        oursMatched += r.ours;
      } else if (cell.status === 'inquire') {
        inquire += 1;
      } else if (r.ours !== 0) {
        // A $0 line (a custom "Travel" note) costs nothing on either side.
        unmatched += 1;
      }
    }
    totals[c.id] = {
      theirs: round2(theirs),
      oursMatched: round2(oursMatched),
      unmatched,
      inquire,
    };
  }

  return { rows, totals, competitors: COMPETITORS, sqft: sqftNum, sqftMissing: sqftNum <= 0 };
}

/**
 * The website order page's size bands (rotoflight-media serviceCatalogV2.js
 * sqftBands): 0 to 999, then 500 sq ft steps to 9,999, then 10,000+. `sqft` is
 * the size a band is priced at: its middle, so a competitor whose rungs end on
 * the thousand (Framed by JG counts 5,000 in their lower rung) prices the bulk
 * of the band, not its first square foot. 10,000+ reads just past 10,000,
 * where every competitor quotes rather than prices.
 */
export const SQFT_BANDS = (() => {
  const n = (v) => v.toLocaleString('en-US');
  const bands = [{ label: '0 to 999', sqft: 500 }];
  for (let t = 1; t <= 9; t++) {
    const lo = t * 1000;
    bands.push({ label: `${n(lo)} to ${n(lo + 499)}`, sqft: lo + 250 });
    bands.push({ label: `${n(lo + 500)} to ${n(lo + 999)}`, sqft: lo + 750 });
  }
  bands.push({ label: '10,000+', sqft: 10001 });
  return bands;
})();

/**
 * Every New-list service and option at one size, against each competitor:
 * the general price check, with no quote behind it.
 *
 * Each row is priced as part of a full order (everything on it at once), so
 * "with another service" prices and "comes with" rules apply the way they do
 * on a real multi-service order. A product also carries what it and theirs
 * cost when it is the whole order (`alone`), where that differs: the 2D Floor
 * Plan without a shoot, Aerial Photos without ground photos, VirTour's twilight
 * without another service. Rows come grouped by our categories, add-ons under
 * the category they attach to.
 */
export function buildPriceListAnalysis(sqft) {
  const sqftNum = Math.floor(Number(sqft) || 0);
  const products = SVC_DB_NEW.categories.flatMap((cat) => cat.products.map((p) => ({ p, cat })));
  const addons = SVC_DB_NEW.addons;
  const ctx = rgPriceContext(products.map(({ p }) => p.id), addons.map((a) => a.id));

  const line = (id, source, desc, price) => ({
    id, productId: id, source, desc, qty: 1, rate: price ?? 0, priceVersion: RG_CURRENT_VERSION,
  });
  const productLines = products.map(({ p }) => line(p.id, 're', p.name, rgGetPrice(p, sqftNum, RG_CURRENT_VERSION, ctx).price));
  const addonLines = addons.map((a) => line(a.id, 're-addon', a.name, rgAddonPrice(a, sqftNum, ctx)));
  const full = buildCompetitionAnalysis({ lines: [...productLines, ...addonLines], sqft: sqftNum });
  const rowOf = new Map(full.rows.map((r) => [r.key, r]));

  const groups = [
    ...SVC_DB_NEW.categories.map((cat) => ({ id: cat.id, name: cat.name, rows: [] })),
    { id: 'any', name: 'Options for any shoot', rows: [] },
  ];
  const groupOf = new Map(groups.map((g) => [g.id, g]));

  for (const { p, cat } of products) {
    const row = rowOf.get(p.id);
    const price = rgGetPrice(p, sqftNum, RG_CURRENT_VERSION, ctx).price;
    const alonePrice = rgGetPrice(p, sqftNum, RG_CURRENT_VERSION, rgPriceContext([p.id], [])).price;
    const solo = buildCompetitionAnalysis({ lines: [line(p.id, 're', p.name, alonePrice)], sqft: sqftNum }).rows[0];
    groupOf.get(cat.id).rows.push({
      ...row,
      quoted: price == null,
      oursAlone: alonePrice !== price ? alonePrice : undefined,
      alone: Object.fromEntries(COMPETITORS.map((c) => [c.id, sameCell(solo.cells[c.id], row.cells[c.id]) ? null : solo.cells[c.id]])),
    });
  }
  for (const a of addons) {
    const row = rowOf.get(a.id);
    // An option that rides on most shoots (the website, branding) gets its own group.
    groupOf.get(a.applies.length > 2 ? 'any' : a.applies[0])?.rows.push({ ...row, quoted: rgAddonPrice(a, sqftNum, ctx) == null,
      unit: a.perImage ? (a.unit === 'each' ? 'each' : 'per photo') : null });
  }

  return { groups: groups.filter((g) => g.rows.length), competitors: COMPETITORS, sqft: sqftNum };
}

// Two cells say the same thing: same status and the same price.
function sameCell(a, b) {
  return a?.status === b?.status && (a?.price ?? null) === (b?.price ?? null);
}
